import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toastSuccess } from '../utils/toasts';
import Button from './atoms/Button';
import Input from './atoms/Input';
import { ShowtimeDTO } from '../models/showtime';
import { useCreateShowtimeMutation, useUpdateShowtimeMutation } from '../store';
import useGetParams from '../hooks/useGetParams';
import { formatDateForInput } from '../utils/dates';
import Switch from './atoms/Switch';

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  showtime?: ShowtimeDTO;
}

interface FormData {
  starts_at: string;
  ends_at: string;
  active: boolean;
}

const validationSchema = yup.object().shape({
  starts_at: yup.string().required('La fecha y hora de inicio es requerida'),
  ends_at: yup.string().required('La fecha y hora de fin es requerida').test(
    'is-after-start',
    'La fecha de fin debe ser posterior a la de inicio',
    (value, testContext) => {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { starts_at } = testContext.parent;
      if (!starts_at || !value) {
        return true;
      }
      return new Date(value) > new Date(starts_at);
    },
  ),
  active: yup.boolean().required('El estado es requerido'),
});

const CreateEditShowtime: React.FC<Props> = ({ onSuccess, onCancel, showtime }) => {
  const { eventId, businessId } = useGetParams();
  const [createShowtime, { isLoading: isCreating, isSuccess: isCreateSuccess }] = useCreateShowtimeMutation();
  const [updateShowtime, { isLoading: isUpdating, isSuccess: isUpdateSuccess }] = useUpdateShowtimeMutation();
  const isLoading = isCreating || isUpdating;

  const {
    control,
    register,
    handleSubmit,
    formState,
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      starts_at: '',
      ends_at: '',
      active: true,
    },
  });

  useEffect(() => {
    if (showtime) {
      reset({
        starts_at: formatDateForInput(showtime.starts_at) || '',
        ends_at: formatDateForInput(showtime.ends_at) || '',
        active: showtime.active,
      });
    }
  }, [showtime, reset]);

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      toastSuccess(`Función ${showtime ? 'actualizada' : 'creada'} correctamente`);
      onSuccess();
    }
  }, [isCreateSuccess, isUpdateSuccess]);

  const onSubmit = async (data: FormData) => {
    if (!eventId || !businessId) {
      return;
    }

    const showtimeData = {
      starts_at: new Date(data.starts_at).toISOString(),
      ends_at: new Date(data.ends_at).toISOString(),
      active: data.active,
    };

    if (showtime) {
      updateShowtime({
        businessId,
        eventId,
        showtimeId: showtime.id,
        showtimeData,
      });
    } else {
      createShowtime({ businessId, eventId, showtimeData });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
      <div className="flex flex-col gap-4">
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <Switch
              label="Activa"
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
            />
          )}
        />
        <Input
          id="starts_at"
          label="Fecha y hora de inicio *"
          type="datetime-local"
          labelAbove
          {...register('starts_at')}
          errMsg={formState.errors.starts_at?.message}
        />
        <Input
          id="ends_at"
          label="Fecha y hora de fin *"
          type="datetime-local"
          labelAbove
          {...register('ends_at')}
          errMsg={formState.errors.ends_at?.message}
        />
      </div>

      <div className="flex justify-end gap-4 mt-8">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" loading={isLoading} disabled={!formState.isValid}>
          Guardar
        </Button>
      </div>
    </form>
  );
};

export default CreateEditShowtime;
